/**
 * @whatItDoes Same as `Map<string, T>`, but with all keys converted to lowercase.
 *
 * @stable
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export class CaseInsensitiveMap<V> implements Map<string, V> {
    [Symbol.toStringTag]: 'Map';

    private readonly inner = new Map<string, V>();

    get(key: string): V | undefined {
        return this.inner.get(convertKey(key));
    }

    set(key: string, value: V): this {
        this.inner.set(convertKey(key), value);
        return this;
    }

    delete(key: string): boolean {
        return this.inner.delete(convertKey(key));
    }

    has(key: string): boolean {
        return this.inner.has(convertKey(key));
    }

    clear() {
        this.inner.clear();
    }

    get size(): number {
        return this.inner.size;
    }

    forEach(callbackFn: (value: V, key: string, map: Map<string, V>) => void, thisArg?: any): void {
        this.inner.forEach((v, k) => callbackFn.call(thisArg, v, k, this));
    }

    [Symbol.iterator]() {
        return this.inner.entries();
    }

    entries() {
        return this.inner.entries();
    }

    keys() {
        return this.inner.keys();
    }

    values() {
        return this.inner.values();
    }
}

export interface CaseInsensitiveMap<V> {
    // eslint-disable-next-line @typescript-eslint/no-misused-new
    new (): CaseInsensitiveMap<V>;
}

function convertKey(key: string): string {
    return key ? key.toLowerCase() : key;
}
